import { Flame, Copy, Crown, LayoutDashboard, BookMarked, QrCode } from "lucide-react";
import { CompanyBoard } from "@/components/CompanyBoard";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LevelBadge } from "@/components/LevelBadge";
import { WeeklyChallenge } from "@/components/WeeklyChallenge";
import { TeamTrophies } from "@/components/TeamTrophies";
import { PhotoChallenge } from "@/components/PhotoChallenge";

const RANK = ["bg-amber-400 text-slate-900", "bg-slate-300 text-slate-900", "bg-orange-300 text-slate-900"];

export function TeamBoard({ team, members, weekly, trophies, photoChallenge, onLeave }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(team.code);
      toast.success("Code copié");
    } catch {
      toast.message(`Code : ${team.code}`);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-slate-900 text-white overflow-hidden rise">
        <div className="hazard-stripe h-2" />
        <div className="p-5 space-y-3">
          <p className="font-display text-3xl font-bold uppercase" data-testid="team-name">{team.name}</p>
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase text-slate-400">Code</span>
            <span className="font-display text-3xl font-extrabold tracking-[0.25em] text-amber-400" data-testid="team-code">{team.code}</span>
            <button onClick={copy} data-testid="team-copy-code-btn" aria-label="Copier le code" className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center transition-colors">
              <Copy className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-slate-300">Partagez ce code avec vos collègues.</p>
          {team.is_owner && (
            <Link to="/equipe/chef" data-testid="team-chef-dashboard-link" className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 font-bold transition-colors">
              <LayoutDashboard className="w-5 h-5" /> Tableau du chef
            </Link>
          )}
        </div>
      </div>

      <CompanyBoard />

      <Link to="/affiche" data-testid="team-poster-link" className="flex items-center gap-3 rounded-2xl bg-white border-2 border-slate-200 hover:border-orange-500 p-4 transition-colors">
        <QrCode className="w-6 h-6 text-orange-600" />
        <span className="flex-1 font-display text-xl font-bold uppercase">Affiche de chantier</span>
        <span className="text-sm text-slate-500">QR code à imprimer</span>
      </Link>

      <Link to="/equipe/lexique" data-testid="team-lexicon-link" className="flex items-center gap-3 rounded-2xl bg-white border-2 border-slate-200 hover:border-orange-500 p-4 transition-colors">
        <BookMarked className="w-6 h-6 text-orange-600" />
        <span className="flex-1 font-display text-xl font-bold uppercase">Lexique de l'équipe</span>
        <span className="text-sm text-slate-500">Nos mots du chantier</span>
      </Link>

      <WeeklyChallenge weekly={weekly} members={members} />
      <PhotoChallenge challenge={photoChallenge} />
      <TeamTrophies trophies={trophies || []} />

      <div className="space-y-2" data-testid="team-leaderboard">
        {members.map((m, i) => (
          <div
            key={`${m.name}-${i}`}
            data-testid={`team-member-${i}`}
            className={`rise flex items-center gap-3 rounded-2xl border-2 p-3 ${m.is_me ? "bg-orange-50 border-orange-400" : "bg-white border-slate-200"}`}
          >
            <span className={`shrink-0 w-10 h-10 rounded-full grid place-items-center font-display text-xl font-bold ${RANK[i] || "bg-slate-100 text-slate-600"}`}>
              {i === 0 ? <Crown className="w-5 h-5" /> : i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{m.name}{m.is_me && " (moi)"}</div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {m.trade} · {m.lessons_completed} leçons <LevelBadge level={m.level} />
              </div>
            </div>
            <div className="flex items-center gap-1 font-display text-2xl font-bold text-orange-600" data-testid={`team-member-streak-${i}`}>
              <Flame className="w-5 h-5" /> {m.streak}
            </div>
          </div>
        ))}
      </div>

      <button onClick={onLeave} data-testid="team-leave-btn" className="w-full h-12 rounded-xl border-2 border-slate-300 text-slate-600 hover:border-red-400 hover:text-red-600 font-bold transition-colors">
        Quitter l'équipe
      </button>
    </div>
  );
}
